<template>
    <v-dialog v-if="canShare" v-model="showDialog" :disabled="isMobileDevice" max-width="450">
        <template #activator="{ props: activatorProps, isActive }">
            <slot name="activator" v-bind="{ props: activatorProps, onShare }">
                <v-btn
                    v-bind="activatorProps"
                    :disabled="isActive"
                    color="primary"
                    variant="outlined"
                    size="small"
                    class="px-2"
                    @click="onShare"
                >
                    <template #prepend>
                        <v-icon :icon="shareIcon" size="20px" />
                    </template>
                    <span class="text-h6">{{ t('trips.resources.tripSharing.shareBtnLabel', 'Share trip') }}</span>
                </v-btn>
            </slot>
        </template>

        <template #default="{ isActive }">
            <v-card rounded="lg">
                <v-card-title class="d-flex pt-6 px-6 pb-0">
                    <span class="text-h3 font-weight-bold">
                        {{ t('trips.resources.tripSharing.dialogTitle', 'Share this trip') }}
                    </span>
                    <v-spacer />
                    <v-btn icon variant="flat" size="32px" @click="isActive.value = false">
                        <v-icon size="32px" color="black">mdi-close-circle</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pb-2">
                    <div v-if="!ready" class="d-flex align-center justify-center pt-4 pb-12">
                        <v-progress-circular color="primary" indeterminate :size="50" />
                    </div>
                    <div v-else>
                        <p class="text-black text-h6 font-weight-regular mb-6">
                            {{
                                t(
                                    'trips.resources.tripSharing.dialogDescription',
                                    'Copy the link below and send to the person you want to share with.'
                                )
                            }}
                        </p>
                        <v-text-field :model-value="shareLink" variant="solo" density="compact" readonly hide-details />
                    </div>
                </v-card-text>

                <v-card-item v-if="ready" class="pb-6">
                    <v-row>
                        <v-col cols="6">
                            <v-btn
                                :text="t('common.resources.back', 'Back')"
                                variant="outlined"
                                color="primary"
                                rounded="lg"
                                size="large"
                                block
                                @click="isActive.value = false"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-btn :text="copyBtnLabel" variant="flat" color="primary" rounded="lg" size="large" block @click="onCopy">
                                <template #prepend>
                                    <v-icon icon="mdi-content-copy" size="24px" />
                                </template>
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-item>
            </v-card>
        </template>
    </v-dialog>
</template>

<script setup lang="ts">
import { useTripSharing } from './useTripSharing';
import { TripPatternDto } from '@/types/webapi';
import { useClipboard, useShare } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { isError } from 'lodash-es';

interface Props {
    tripPattern: TripPatternDto;
}

const props = defineProps<Props>();
const { t } = useI18n();
const ready = ref(false);
const showDialog = ref(false);
const showCopied = ref(false);
const { platform } = useDisplay();
const isMobileDevice = computed(() => platform.value.ios || platform.value.android);
const { canShare, saveTripPattern, shareLink } = useTripSharing(props);
const { copy } = useClipboard();
const { share, isSupported } = useShare();
const copyBtnLabel = computed(() =>
    showCopied.value
        ? t('trips.resources.tripSharing.copiedBtnLabel', 'Copied!')
        : t('trips.resources.tripSharing.copyBtnLabel', 'Copy link')
);
const shareIcon = computed(() => (platform.value.ios ? 'mdi-export-variant' : 'mdi-share-variant-outline'));

async function onShare() {
    // if share is not supported, show dialog
    if (!isSupported.value) {
        showDialog.value = true;
    }

    // save trip pattern
    await saveTripPattern();
    ready.value = true;

    // share with share api if supported and mobile device
    if (isMobileDevice.value && isSupported.value) {
        const origin = props.tripPattern.legs?.[0].from.name;
        const destination = props.tripPattern.legs?.[props.tripPattern.legs.length - 1].to.name;

        try {
            await share({
                title: t('trips.resources.tripSharing.title', { origin, destination }),
                url: shareLink.value
            });
        } catch (e) {
            // ignore abort share error
            if (!isError(e) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }
}

function onCopy() {
    if (!shareLink.value) return;

    copy(shareLink.value);

    showCopied.value = true;
    setTimeout(() => (showCopied.value = false), 3000);
}
</script>
